<template>
  <div class="grid-card" :class="cardClasses" :style="cssVars">
    <header v-if="temHeader" class="grid-card__header">
      <div class="grid-card__titulo">
        <slot name="titulo">
          <strong v-if="titulo">{{ titulo }}</strong>
        </slot>
        <span v-if="subtitulo" class="grid-card__subtitulo">{{ subtitulo }}</span>
      </div>
      <div v-if="$slots.meta" class="grid-card__meta">
        <slot name="meta" />
      </div>
    </header>

    <div class="grid-card__corpo">
      <component
        v-if="componente"
        :is="componente"
        v-bind="componenteProps"
      />
      <slot v-else />
    </div>
  </div>
</template>

<script>
/**
 * Card padrão para exibição de dados.
 *
 * Pode receber conteúdo de 3 formas (em ordem de prioridade):
 *  1. Prop `componente` — renderiza componente Vue passado via prop
 *  2. Slot `default` — conteúdo arbitrário
 *
 * Layout:
 *  - Posicionado dentro de <GridCardRow> ocupando `cols` colunas (1..12)
 *  - Em mobile, vira 1 coluna (responsive)
 *
 * Exemplo (slot):
 *  <GridCard :cols="6" titulo="Tabela #1" subtitulo="Atacado">
 *    <template #meta>
 *      <span class="sistema-badge sistema-badge--ativo">Ativo</span>
 *    </template>
 *    <GridCardSection label="Custo">
 *      <GridCardField label="CMV" :valor="12.50" tipo="monetary" />
 *    </GridCardSection>
 *  </GridCard>
 *
 * Exemplo (componente customizado):
 *  <GridCard
 *    :cols="6"
 *    titulo="Gráfico"
 *    :componente="MeuGrafico"
 *    :componenteProps="{ serie: [...] }"
 *  />
 */
export default {
  name: "GridCard",
  props: {
    cols: { type: [Number, String], default: 12 },
    titulo: { type: String, default: null },
    subtitulo: { type: String, default: null },
    accent: { type: Boolean, default: false },
    sombreado: { type: Boolean, default: false },
    componente: { type: [Object, Function], default: null },
    componenteProps: { type: Object, default: () => ({}) },
  },
  computed: {
    temHeader() {
      return !!(this.titulo || this.subtitulo || this.$slots.titulo || this.$slots.meta);
    },
    cardClasses() {
      return {
        "grid-card--accent": this.accent,
        "grid-card--sombreado": this.sombreado,
      };
    },
    cssVars() {
      const colsNumero = Math.max(1, Math.min(12, Number(this.cols) || 12));
      return {
        "grid-column": `span ${colsNumero}`,
      };
    },
  },
};
</script>

<style scoped>
.grid-card {
  background: #fff;
  border: 1px solid #e4e7eb;
  border-radius: 10px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.grid-card--sombreado {
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.05);
}

.grid-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  background: #f9fafb;
  border-bottom: 1px solid #e4e7eb;
  flex-wrap: wrap;
}

.grid-card--accent .grid-card__header {
  background: linear-gradient(135deg, rgba(58, 62, 129, 0.06), rgba(34, 152, 163, 0.06));
}

.grid-card__titulo {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.grid-card__titulo strong {
  color: #1d2939;
  font-size: 14px;
}

.grid-card__subtitulo {
  font-size: 12px;
  color: #667085;
}

.grid-card__meta {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  font-size: 12px;
  color: #667085;
}

.grid-card__corpo {
  flex: 1;
  display: flex;
  flex-direction: column;
}

@media (max-width: 768px) {
  .grid-card {
    grid-column: span 1 !important;
  }
}
</style>
